/**
 * Main App Component
 *
 * Root component that orchestrates authentication and routing.
 * DataProvider wraps the router so inventory + branch data persists
 * across page navigations (no refetch on route change).
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { DealershipSettingsProvider } from './contexts/DealershipSettingsContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import SettingsUpdateNote from './components/shared/SettingsUpdateNote';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import LandingPage from './components/LandingPage/LandingPage';
import WorkspaceLogin from './components/Login/WorkspaceLogin';
import TenantLogin from './components/Login/TenantLogin';
import InventoryDashboard from './components/InventoryDashboard';
import BranchOverview from './components/BranchOverview/BranchOverview';
import TaskBoard from './components/TaskBoard/TaskBoard';
import AdminConsole from './components/AdminConsole/AdminConsole';
import apiService from './services/apiService';
import {
    ADMIN_SEEN_KEY, PENDING_UNIT_KEY, RETURN_PATH_KEY, STAY_ON_LANDING_KEY, WORKSPACE_SLUG_KEY, clearStayOnLanding,
} from './config/sessionKeys';
import SessionExpiredPrompt from './components/SessionExpiredPrompt/SessionExpiredPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import AppTopBar from './components/AppShell/AppTopBar';
import AccessDenied from './components/shared/AccessDenied';
import UiKitPage from './components/UiKit/UiKitPage';
import UnitRouteV2 from './components/UnitV2/UnitRouteV2';
import { tabFromSearch } from './utils/unitRoute';
import {
    arrivedWithUiParam, forgetUiParamArrival, isUiKitAvailable, useUiV2,
} from './config/uiVersion';
import './styles/global.css';

const readSession = (key) => {
    // Storage can be unavailable (private mode, blocked cookies).
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
};

// Tenant view (the PricePath dashboard). Super/dealer admins bounce to the
// admin console on their first load of the session, then can switch freely.
// A deep link wins over the bounce: a URL that carries a query (a shared,
// filtered inventory view) or a return path saved at session expiry is where
// the user meant to go. The bounce then waits for the next plain load. So is
// the v1 "open this unit" handoff (PENDING_UNIT_KEY, written by the Task Board
// card click and by a v1 /unit/:stock link before they send the user to `/`,
// QA T-11). And so is a link that carried the design flag (?ui=v1|v2): index.js
// strips `ui` from the address before this reads it, so a bare `/?ui=v2` would
// otherwise look like a plain load (config/uiVersion.js arrivedWithUiParam).
function TenantHome({ me, user, logout, isAuthenticated }) {
    const navigate = useNavigate();
    // Read once, at arrival: params the dashboard itself writes later (from
    // saved filters) must not count as a deep link.
    const [deepLinked] = useState(
        () => Boolean(window.location.search) || arrivedWithUiParam()
            || Boolean(readSession(RETURN_PATH_KEY)) || Boolean(readSession(PENDING_UNIT_KEY)),
    );
    useEffect(() => {
        if (deepLinked) return;
        if (me?.is_admin && !sessionStorage.getItem(ADMIN_SEEN_KEY)) {
            sessionStorage.setItem(ADMIN_SEEN_KEY, '1');
            navigate('/admin', { replace: true });
        }
    }, [me, navigate, deepLinked]);
    return (
        <InventoryDashboard
            onLogout={logout}
            user={user}
            isAuthenticated={isAuthenticated}
            isAdmin={me?.is_admin}
            orgName={me?.org_name}
            onOpenAdmin={() => navigate('/admin')}
        />
    );
}

// /unit/:stock with the new design off: today's pane has no route, so the
// link opens it the way the Task Board does (sessionStorage handshake, the
// inventory opens the unit on the tab once its data is in).
function UnitLinkV1() {
    const { stock } = useParams();
    const { search } = useLocation();
    try {
        if (stock) sessionStorage.setItem(PENDING_UNIT_KEY, JSON.stringify({ stockNumber: stock, tab: tabFromSearch(search) }));
    } catch (e) { /* storage blocked: the inventory opens without the unit */ }
    return <Navigate to="/" replace />;
}

function AdminRoute({ me, logout }) {
    const navigate = useNavigate();
    const { requestWorkspace } = useWorkspace();

    // SAG staff administer several dealerships, so "IronSight view" cannot know
    // which one they mean — ask, unless there is only one answer. Backing out of
    // the question leaves them in the console rather than dumping them into a
    // dashboard they did not choose.
    const switchToApp = async () => {
        const chosen = await requestWorkspace();
        if (chosen) navigate('/');
    };

    // `me` is null whenever /me failed for anything other than a 403 — a network
    // blip, a 500 — and treating that silence as permission let the console render
    // and fire its admin requests. Only a confirmed admin gets through.
    if (!me?.is_admin) return <Navigate to="/" replace />;
    return <AdminConsole me={me} onSwitchToApp={switchToApp} onLogout={logout} />;
}

// The routes that exist only for a signed-in user, under the dealership they
// are currently looking at.
function SignedInRoutes({ me, user, logout, isAuthenticated }) {
    const { activeSlug } = useWorkspace();
    const navigate = useNavigate();
    // "New design" flag (config/uiVersion.js): always on unless the build sets
    // REACT_APP_ENABLE_V1=true (rollback, docs/decisions/v2-only.md). With it
    // on, the v2 top bar replaces the pages' own headers and every route below
    // renders its v2 page; the v1 pages stay only for that rollback.
    const uiV2 = useUiV2();

    // Signed back in after the session expired and the page was reloaded (so the
    // sign-in went through /login/:slug): return to where they were.
    useEffect(() => {
        const returnPath = readSession(RETURN_PATH_KEY);
        if (!returnPath) return;
        try { sessionStorage.removeItem(RETURN_PATH_KEY); } catch (e) { /* nothing stored */ }
        navigate(returnPath, { replace: true });
    }, [navigate]);

    return (
        // Keyed on the dealership: every cache below this point — inventory,
        // branch rollups, saved filters, activities — belongs to one tenant, and
        // switching has to discard them rather than show the previous dealer's
        // numbers under the new name.
        // The user's own settings (column layout) follow the user, not the
        // dealership: above the keyed DataProvider, loaded once per sign-in.
        <UserSettingsProvider scope={activeSlug || null}>
        <DataProvider key={activeSlug || 'default'}>
            {/* The dealership's settings (aging buckets, gap scale, APR, margin
                floor): per dealership, so under the keyed provider. */}
            <DealershipSettingsProvider>
            {uiV2 && <AppTopBar me={me} user={user} onLogout={logout} />}
            {uiV2 && <SettingsUpdateNote />}
            <Routes>
                <Route path="/" element={<TenantHome me={me} user={user} logout={logout} isAuthenticated={isAuthenticated} />} />
                <Route path="/admin" element={<AdminRoute me={me} logout={logout} />} />
                <Route path="/branches" element={<BranchOverview onLogout={logout} user={user} orgName={me?.org_name} />} />
                <Route path="/tasks" element={<TaskBoard onLogout={logout} user={user} orgName={me?.org_name} />} />
                {/* The unit detail as a full page (U1) with the new design on. */}
                <Route path="/unit/:stock" element={uiV2 ? <UnitRouteV2 /> : <UnitLinkV1 />} />
                {isUiKitAvailable() && <Route path="/ui-kit" element={<UiKitPage />} />}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </DealershipSettingsProvider>
        </DataProvider>
        </UserSettingsProvider>
    );
}

// A signed-out visitor opened an app page (a shared unit link, a QR code):
// remember it the way session expiry does (RETURN_PATH_KEY), so SignedInRoutes
// takes them there after they sign in (QA L-2). Then on to the landing page.
function RememberDeepLink() {
    // Read from the address bar: this renders once, on arrival at that URL.
    const { pathname, search } = window.location;
    try { sessionStorage.setItem(RETURN_PATH_KEY, `${pathname}${search}`); } catch (e) { /* storage blocked: they land on the inventory */ }
    return <Navigate to="/" replace />;
}

// Pre-auth routes: marketing landing + the WT-style two-step workspace login.
// "Sign in" on the landing sends the user to the slug-entry screen.
function PublicRoutes({ login, isLoading, error }) {
    const navigate = useNavigate();
    const location = useLocation();
    // A session that expired keeps its workspace: send the user straight to that
    // workspace's sign-in rather than the marketing page and the slug prompt.
    // Except right after an explicit "← Back to IronSight" (state stayOnLanding,
    // QA L-10): that visit shows the landing page. Nothing saved is cleared, so a
    // later sign-in still resumes.
    const stayOnLanding = Boolean(location?.state?.stayOnLanding) || Boolean(readSession(STAY_ON_LANDING_KEY));
    const resumeSlug = !stayOnLanding && readSession(RETURN_PATH_KEY) ? readSession(WORKSPACE_SLUG_KEY) : null;
    // Signed out, a link with the design flag leads to the sign-in like any
    // other visit: what they land on after it is decided by the sign-in (a
    // remembered app page, else the admin first-load rule), not by `?ui=`.
    useEffect(() => { forgetUiParamArrival(); }, []);
    return (
        <Routes>
            <Route
                path="/"
                element={resumeSlug
                    ? <Navigate to={`/login/${encodeURIComponent(resumeSlug)}`} replace />
                    : <LandingPage onSignIn={() => { clearStayOnLanding(); navigate('/login'); }} isLoading={isLoading} error={error} />}
            />
            <Route path="/login" element={<WorkspaceLogin />} />
            <Route path="/login/:slug" element={<TenantLogin onSignIn={login} isLoading={isLoading} error={error} />} />
            {/* The redesign's living UI kit: static sample content, no data. */}
            {isUiKitAvailable() && <Route path="/ui-kit" element={<UiKitPage />} />}
            <Route path="*" element={<RememberDeepLink />} />
        </Routes>
    );
}

function App() {
    const {
        isAuthenticated, user, isLoading, error, login, logout,
        sessionExpired, expireSession, reauthenticate,
    } = useAuth();
    const [me, setMe] = useState(null);
    const [meLoading, setMeLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);

    // Resolve the caller's identity/role from sag_identity once authenticated.
    // A 403 here means they authenticated with Entra but have no active PricePath
    // access — we show the access screen instead of the app.
    useEffect(() => {
        if (!isAuthenticated) { setMe(null); setMeLoading(true); setAccessDenied(false); return; }
        let alive = true;
        setMeLoading(true); setAccessDenied(false);
        apiService.getMe()
            .then((m) => { if (alive) setMe(m); })
            .catch((err) => { if (alive) { setMe(null); if (err?.status === 403) setAccessDenied(true); } })
            .finally(() => { if (alive) setMeLoading(false); });
        return () => { alive = false; };
    }, [isAuthenticated]);

    // If access is revoked mid-session (a data route 403s), show the access screen.
    useEffect(() => {
        const onRevoked = () => setAccessDenied(true);
        window.addEventListener('pp:access-revoked', onRevoked);
        return () => window.removeEventListener('pp:access-revoked', onRevoked);
    }, []);

    // The token could not be renewed without the user. This used to sign them out —
    // a logout popup, the workspace forgotten, the page gone. Now the session is
    // marked expired and a prompt offers to sign back in; the page, the workspace
    // and everything open on it stay put.
    useEffect(() => {
        const onExpired = () => { expireSession().catch(() => {}); };
        window.addEventListener('pp:session-expired', onExpired);
        return () => window.removeEventListener('pp:session-expired', onExpired);
    }, [expireSession]);

    if (isLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <BrowserRouter>
                {isAuthenticated ? (
                    meLoading ? (
                        <div className="loading"><div className="spinner"></div></div>
                    ) : accessDenied ? (
                        <AccessDenied onSignOut={logout} />
                    ) : (
                        // Mounted only inside the signed-in branch, so signing out
                        // unmounts it and the fetched dealership list goes with it.
                        <WorkspaceProvider>
                            <SignedInRoutes me={me} user={user} logout={logout} isAuthenticated={isAuthenticated} />
                        </WorkspaceProvider>
                    )
                ) : (
                    <PublicRoutes login={login} isLoading={isLoading} error={error} />
                )}
                {isAuthenticated && sessionExpired && <SessionExpiredPrompt onSignIn={reauthenticate} />}
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
